import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Phone, Plus, Check, X, CheckCircle2 } from "lucide-react";

const STATUS_CLS = {
  proposed: "border-[#FF8800]/40 text-[#FF8800]",
  confirmed: "border-[#00B0FF]/40 text-[#00B0FF]",
  completed: "border-[#00E676]/40 text-[#00E676]",
  declined: "border-red-500/40 text-red-400",
  cancelled: "border-slate-600/60 text-slate-500",
};

export function CallsPanel({ orderId, myId, testid = "calls-panel" }) {
  const [calls, setCalls] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ scheduled_at: "", agenda: "", duration_minutes: "30" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/portal/orders/${orderId}/calls`).then((r) => setCalls(r.data)).catch(() => {});
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const propose = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/portal/orders/${orderId}/calls`, { ...form, duration_minutes: Number(form.duration_minutes) });
      toast.success("Call proposed — the other side has been notified");
      setForm({ scheduled_at: "", agenda: "", duration_minutes: "30" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const act = async (call, action) => {
    try {
      await api.put(`/portal/calls/${call.id}`, { action });
      toast.success(`Call ${action === "confirm" ? "confirmed" : action === "decline" ? "declined" : action === "complete" ? "marked completed" : "cancelled"}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="glass rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="font-display font-semibold text-white text-sm flex items-center gap-2">
            <Phone className="w-4 h-4 text-[#00B0FF]" /> Scheduled Calls
          </p>
          <p className="text-[10px] font-mono-tech tracking-widest text-slate-500 uppercase mt-0.5">Founder ↔ Specialist</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setOpen((v) => !v)} data-testid={`${testid}-propose-open`} className="border-white/15 text-slate-300 hover:bg-white/5">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Propose Call
        </Button>
      </div>

      {open && (
        <form onSubmit={propose} className="rounded-xl border border-white/10 p-4 space-y-3 mb-4" data-testid={`${testid}-form`}>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs">Date & Time *</Label>
              <Input data-testid={`${testid}-datetime`} type="datetime-local" required value={form.scheduled_at} onChange={(e) => setForm((f) => ({ ...f, scheduled_at: e.target.value }))} className="bg-[#06080E] border-white/10 h-9 text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs">Duration</Label>
              <Select value={form.duration_minutes} onValueChange={(v) => setForm((f) => ({ ...f, duration_minutes: v }))}>
                <SelectTrigger data-testid={`${testid}-duration`} className="bg-[#06080E] border-white/10 text-slate-200 h-9"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0D111D] border-white/10 text-slate-200">
                  {["15", "30", "45", "60"].map((d) => <SelectItem key={d} value={d}>{d} minutes</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Agenda</Label>
            <Input data-testid={`${testid}-agenda`} value={form.agenda} onChange={(e) => setForm((f) => ({ ...f, agenda: e.target.value }))} placeholder="e.g. Discuss document verification status" className="bg-[#06080E] border-white/10 h-9 text-sm" />
          </div>
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={saving} data-testid={`${testid}-propose-submit`} className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold">
              {saving ? "Sending..." : "Send Proposal"}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setOpen(false)} className="border-white/15 text-slate-300">Cancel</Button>
          </div>
        </form>
      )}

      {calls.length === 0 ? (
        <p className="text-center text-xs text-slate-500 py-5" data-testid={`${testid}-empty`}>No calls scheduled yet. Propose a time that works for you.</p>
      ) : (
        <div className="space-y-2.5" data-testid={`${testid}-list`}>
          {calls.map((c) => (
            <div key={c.id} className="rounded-xl border border-white/10 px-4 py-3" data-testid={`call-${c.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-white">{c.scheduled_at.replace("T", " ").slice(0, 16)} · {c.duration_minutes} min</p>
                    <span className={`text-[10px] font-mono-tech px-2 py-0.5 rounded-full border ${STATUS_CLS[c.status] || STATUS_CLS.proposed}`}>{c.status.toUpperCase()}</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5">Proposed by {c.proposed_by}{c.agenda ? ` · ${c.agenda}` : ""}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  {c.status === "proposed" && c.proposed_by_id !== myId && (
                    <>
                      <Button size="sm" variant="outline" data-testid={`call-confirm-${c.id}`} onClick={() => act(c, "confirm")} className="border-[#00E676]/30 text-[#00E676] hover:bg-[#00E676]/10 h-8 text-xs">
                        <Check className="w-3.5 h-3.5 mr-1" /> Confirm
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`call-decline-${c.id}`} onClick={() => act(c, "decline")} className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8 text-xs">
                        <X className="w-3.5 h-3.5 mr-1" /> Decline
                      </Button>
                    </>
                  )}
                  {c.status === "confirmed" && (
                    <Button size="sm" variant="outline" data-testid={`call-complete-${c.id}`} onClick={() => act(c, "complete")} className="border-[#00E676]/30 text-[#00E676] hover:bg-[#00E676]/10 h-8 text-xs">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark Done
                    </Button>
                  )}
                  {(c.status === "proposed" || c.status === "confirmed") && (
                    <Button size="sm" variant="outline" data-testid={`call-cancel-${c.id}`} onClick={() => act(c, "cancel")} className="border-white/15 text-slate-400 hover:bg-white/5 h-8 text-xs">Cancel</Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
